import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Grid, Border, Breakpoints, Color, Shadow, Type } from '../app/StyleGuide';
import ErrorMessage from './ErrorMessage';

export const Modal = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${Color.white};
  box-shadow: ${Shadow.regular};
  text-align: center;
  margin: 0 auto;
  border-radius: ${Border.radius};
  width: 100%;
  max-width: 600px;
  max-height: calc(100vh - ${Grid._6}); /* viewport height - 2x padding */

  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
  }

  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    max-width: 80%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
  }
`;

export const ModalHeader = styled.div`
  color: #fff;
  background-color: ${Color.Gray._90};
  padding: 0 ${Grid._5};
  border-radius: ${Border.radius} ${Border.radius} 0 0;
  text-align: left;
  flex-shrink: 0;

  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    border-radius: 0;
  }

  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    border-radius: 0;
  }

  h3 {
    display: flex;
    align-items: center;
    color: inherit;
    text-shadow: ${Type.Shadow.dark};
    font-size: ${Type.Scale._4};
    font-weight: ${Type.Weight.semibold};
    margin: ${Grid._4} 0;
  }
`;

export const ModalBody = styled.div<{ preventScroll?: boolean }>`
  text-align: left;
  padding: ${Grid._4};
  color: ${Type.Color.medium};
  overflow-y: ${props => (props.preventScroll ? 'visible' : 'auto')};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    padding: ${Grid._5};
  }

  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    padding: ${Grid._3};
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  h3 {
    color: ${Type.Color.dark};
    font-weight: ${Type.Weight.semibold};
    font-size: ${Type.Scale._4};
    margin: 0;
  }
  h4 {
    color: ${Type.Color.dark};
    font-size: ${Type.Scale._3};
    font-weight: ${Type.Weight.normal};
    margin-top: 0;
  }
  .modal-component-subtext {
    color: ${Type.Color.dark};
    font-size: ${Type.Scale._3};
    font-weight: ${Type.Weight.semibold};
  }
  .modal-component-state-message {
    font-size: ${Type.Scale._1};
    padding-left: ${Grid._4};
    position: relative;
    margin: ${Grid._3} 0 0;
    .icon {
      font-size: ${Type.Scale._2};
      margin-right: ${Grid._1};
      color: ${Color.Red._50};
      position: absolute;
      top: -1px;
      left: 0;
    }
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0;
  background: ${Color.Gray._05};
  padding: ${Grid._4} ${Grid._5};
  border-radius: 0 0 ${Border.radius} ${Border.radius};
  flex-shrink: 0;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    flex-direction: row;
  }

  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    border-radius: 0;
  }

  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    border-radius: 0;
    padding: ${Grid._3};
  }

  button {
    width: 100%;

    & + button {
      margin: ${Grid._3} 0 0 0;
    }

    @media screen and (min-width: ${Breakpoints.screen_sm}) {
      width: inherit;
      & + button {
        margin: 0 0 0 ${Grid._4};
      }
    }
  }
`;

const Background = styled.div`
  position: fixed;
  height: 100vh;
  height: 100dvh;
  height: -webkit-fill-available;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2147483004; /* bring it above Intercom */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Grid._4};

  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    padding: 0;
  }

  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    padding: 0;
    height: 100vh;
    height: 100dvh;
  }
`;

const WarningListItem = styled(ErrorMessage)`
  justify-content: flex-start;
  background: none;
  border: none;
`;

type ModalBackgroundProps = {
  children: ReactNode;
  dismissHandler: () => void;
  className?: string;
  id?: string;
  disableBackgroundDismiss?: boolean;
};

export const ModalWarningList = ({ warnings }: { warnings: string[] }) =>
  warnings.map(warning => <WarningListItem key={warning}>{warning}</WarningListItem>);

export const ModalBackground = ({
  children,
  dismissHandler,
  className,
  id = 'background',
  disableBackgroundDismiss = false,
}: ModalBackgroundProps): React.ReactElement => {
  const backgroundRef = useRef<HTMLDivElement>(null);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (disableBackgroundDismiss) return;

    if (backgroundRef.current && e.target === backgroundRef.current) {
      dismissHandler();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      dismissHandler();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return createPortal(
    <Background
      data-qa-hook="modalBackground"
      id={id}
      className={className}
      ref={backgroundRef}
      onClick={handleBackgroundClick}
    >
      {children}
    </Background>,
    document.body
  ) as React.ReactElement;
};

export const ModalContainer = ({
  children,
  ...props
}: {
  children: ReactNode;
  dismissHandler: () => void;
  disableBackgroundDismiss?: boolean;
}) => (
  <ModalBackground {...props}>
    <Modal role="alertdialog">{children}</Modal>
  </ModalBackground>
);
